import { useMemo } from 'react';
import { 
    Box,
    Typography
  } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { AdcashAvatyeDetailMetrics } from '../../../../../types/adcash.types';

// 색상 팔레트
const COLORS = [
  '#0044D2',
  '#D32F2F',
  '#1976D2',
  '#388E3C',
  '#F57C00',
  '#7B1FA2',
  '#C2185B',
  '#0097A7'
];

type Props = {
    data: AdcashAvatyeDetailMetrics;
    selectedPlacement: string;
    selectedApp: string | null;
    exchangeRate: number;
}

export default function AdcashAvatyeDetailMetricsChart(
  { data, selectedPlacement, selectedApp, exchangeRate }: Props
) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  
  // 선택된 지면으로 필터링
  const filteredStats = useMemo(() => {
    if (selectedPlacement === '전체') {
      return data.dailyStats;
    }
    return data.dailyStats.filter(stat => stat.placementGroup === selectedPlacement);
  }, [data.dailyStats, selectedPlacement]);

  // 미디에이션 목록 추출
  const mediationList = useMemo(() => {
    const mediations = new Set<string>();
    filteredStats.forEach(stat => {
      if (stat.mediation) {
        mediations.add(stat.mediation);
      }
    });
    return Array.from(mediations).sort();
  }, [filteredStats]);

  // 미디에이션별로 날짜별 데이터 그룹화
  const chartData = useMemo(() => {
    // 모든 날짜 수집
    const allDates = new Set<string>();
    filteredStats.forEach(stat => {
      allDates.add(stat.date);
    });
    const sortedDates = Array.from(allDates).sort();

    // 항상 미디에이션별로 표시
    const dateDataMap: Record<string, Record<string, number | string>> = {};
    
    sortedDates.forEach(date => {
      dateDataMap[date] = { date };
      
      mediationList.forEach(mediation => {
        const mediationStats = filteredStats.filter(
          stat => stat.date === date && stat.mediation === mediation
        );
        const mediationRevenue = mediationStats.reduce((sum, stat) => sum + stat.revenue, 0) * exchangeRate;
        dateDataMap[date][mediation] = mediationRevenue;
      });
    });

    return Object.values(dateDataMap);
  }, [filteredStats, mediationList, exchangeRate]);

  return (
    <Box sx={{ display: 'flex', gap: 3, mt: 2, minWidth: 0 }}>
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          backgroundColor: '#ffffff',
          borderRadius: 2,
          p: 3,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: 'center' }}>
          미디에이션별 매출 추이
        </Typography>
        <ResponsiveContainer width="100%" height={210}>
          <AreaChart data={chartData}>
            <defs>
              {/* 미디에이션별 그라데이션 */}
              {mediationList.map((mediation, index) => (
                <linearGradient key={mediation} id={`color${mediation.replace(/\s+/g, '')}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS[index % COLORS.length]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <XAxis 
              dataKey="date"
              tickFormatter={(value: string) => {
                const date = new Date(value);
                return `${date.getMonth() + 1}/${date.getDate()}`;
              }}
            />
            <YAxis 
              tickFormatter={(value: number) => {
                if (value === 0) return '0';
                return `${(value / 1000).toFixed(0)}K`;
              }} 
            />
            <RechartsTooltip
              formatter={(value: number, name: string) => [formatCurrency(value), name]}
              labelFormatter={(value: string) =>
                new Date(value).toLocaleDateString('ko-KR')
              }
            />
            <Legend />
            {/* 미디에이션별 매출 - 스택형으로 표시 */}
            {mediationList.map((mediation, index) => (
              <Area
                key={mediation}
                type="monotone"
                dataKey={mediation}
                stackId="1"
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#color${mediation.replace(/\s+/g, '')})`}
                name={mediation}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
